// Pointy-top hexagon geometry in axial coords (q, r). Pure math, no dependencies.

export interface Axial {
  q: number;
  r: number;
}

const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export const hexKey = (q: number, r: number): string => `${q},${r}`;

export function hexNeighbors(q: number, r: number): Axial[] {
  return DIRS.map(([dq, dr]) => ({ q: q + dq, r: r + dr }));
}

/** Axial hex distance. */
export function hexDistance(a: Axial, b: Axial): number {
  return (Math.abs(a.q - b.q) + Math.abs(a.q + a.r - b.q - b.r) + Math.abs(a.r - b.r)) / 2;
}

/** Axial → pixel centre (pointy-top). */
export function axialToPixel(q: number, r: number, size: number): { x: number; y: number } {
  return { x: size * Math.sqrt(3) * (q + r / 2), y: size * 1.5 * r };
}

/** The 6 corner points of a pointy-top hex as a flat [x0,y0,x1,y1,...] array. */
export function hexCorners(cx: number, cy: number, size: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 30);
    pts.push(cx + size * Math.cos(a), cy + size * Math.sin(a));
  }
  return pts;
}

function hexRound(qf: number, rf: number): Axial {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
}

/** Pixel → nearest axial hex (pointy-top), rounded. */
export function pixelToAxial(x: number, y: number, size: number): Axial {
  const qf = ((Math.sqrt(3) / 3) * x - (1 / 3) * y) / size;
  const rf = ((2 / 3) * y) / size;
  return hexRound(qf, rf);
}
